import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const financeSectionDiagramAgile = '/images/aip/Finance/finance/agile-adaptation-overview.png';
const financeSectionDiagramBizFinance = '/images/aip/Finance/finance/biz-finance-deep-integration.png';
const financeSectionDiagramCore = '/images/aip/Finance/finance/core-scenarios-closure.png';
const financeSectionDiagramEngine = '/images/aip/Finance/finance/accounting-engine-overview.png';
const bannerBg = '/images/aip/Finance/finance-banner-bg.svg';
const case01 = '/images/aip/Finance/case-01.png';
const case02 = '/images/aip/Finance/case-02.png';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '敏捷适配',
    subtitle: '消除业务多元化带来的“数据噪音”',
    previewAlt: '敏捷适配示意图',
    previewImage: financeSectionDiagramAgile,
    items: [
      {
        title: '全口径数据归集',
        description:
          '支持对接采购、销售、项目等多业务系统，将业务数据归集为统一待结算数据，通过后续联动配置实现往来管理同步。'
      },
      {
        title: '价值实时感知',
        description:
          '每一笔出入库动作即刻同步成本变动。CFO 无需等待期末结账，随时掌握真实的存货价值与利润水位，确保账实始终如一。'
      },
      {
        title: '多业态柔性兼容',
        description:
          '面对多组织、多币种、多场景都复杂业态，通过规则引擎灵活定义结算逻辑，让新业务上线即刻高效兼容。'
      }
    ]
  },
  {
    title: '业财深度融合',
    subtitle: '构建透明可信且自动化的核算链路',
    previewAlt: '业财深度融合示意图',
    previewImage: financeSectionDiagramBizFinance,
    items: [
      {
        title: '管理需求可配置',
        description:
          '可根据自身管理需求，灵活定义业务逻辑如何映射到财务核算，构建符合企业战略的专属业财核算体系。'
      },
      {
        title: '多维核算映射',
        description:
          '摆脱单一限制，支持按组织、项目、合同、产品线等管理维度自动归集，满足集团化企业复杂管理报表需求，看清细分维度盈利能力。'
      },
      {
        title: '全链路可追溯',
        description:
          '每一张凭证都能回溯至最初的业务现场。可视化路径让财务看懂业务，业务理解合规，大幅降低内部跨部门沟通成本。'
      }
    ]
  },
  {
    title: '核心场景闭环',
    subtitle: '稳健有效防范资金与成本风险',
    previewAlt: '核心场景闭环示意图',
    previewImage: financeSectionDiagramCore,
    items: [
      {
        title: '智能往来管理',
        description: '自动钩稽、账龄预警、风险计提。实时监控往来风险，加速资金回笼，保障企业现金流安全。'
      },
      {
        title: '精准成本管控',
        description:
          '内置多种科学计价模型，从业务发生到期末结转全自动完成，确保成本计算不失真，为产品定价提供精准依据。'
      },
      {
        title: '资金链路可视',
        description:
          '集成银行对账与资金计划，实现资金的实时监控。让总部对集团的每一分钱都拥有绝对的掌控力和调度权。'
      }
    ]
  },
  {
    title: '会计引擎',
    subtitle: '保护现有IT资产的“超级枢纽”',
    previewAlt: '会计引擎示意图',
    previewImage: financeSectionDiagramEngine,
    items: [
      {
        title: '低耦合无感升级',
        description: '引擎独立于总账系统部署，无需替换现有的财务软件，即可获得业财中台能力，保护既有 IT 投资。'
      },
      {
        title: '主流系统无缝对接',
        description:
          '无论是 SAP、Oracle 还是用友、金蝶，会计引擎都能将标准化核算数据精准输入总账，免去繁琐的定制化开发。'
      },
      {
        title: '数据口径高度一致',
        description:
          '消除各业务部门自说自话的乱象，将会计准则前置到业务端，确保输出的每一条数据都符合合规标准。'
      },
      {
        title: '审计级合规存证',
        description: '每一笔输出数据均保留完整的业务来源与转换规则记录，让审计轻松追溯，应对内外部监管。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Finance() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;
    const note =
      '打破业务与财务的壁垒，提升企业多组织业财数据的标准化、自动化程度并实现新场景的业财快速支持能力，让繁杂的底层数据支持企业经营，让企业在复杂多变的市场中始终保持精准洞察。';

    const noteTips: NoteTip[] = [
      { value: '+95%', label: '业财数据标准化' },
      { value: '+80%', label: '核算流程自动化' },
      { value: '+50%', label: '新业务接入效率' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '业务应用-财务',
      subheading: '',
      legend: '缩小业财鸿沟，让每一笔业务决策都有据可依。',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, [isPc]);

  return (
    <div className="finance-page">
      <div className="aip-banner-wrap finance-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">龙头企业数字化升级与业财一体化实践</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">三力士 中国橡胶V带制造企业龙头</h3>
                    <p className="case-desc">以“生产制造”为核心，助力企业供应链整体数字化升级。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="三力士案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
              <a className="case-link" href="https://terminus.io/case-detail/2006001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 02</span>
                  <div className="case-body">
                    <h3 className="case-title">新华书店 中国图书发行龙头企业</h3>
                    <p className="case-desc">整体业务升级，支撑多渠道大数据量整合及统一结算分析。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case02} alt="新华书店案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
